import { useQuery } from '@tanstack/react-query';
import {
  loadConcelhos,
  loadDistritos,
  loadLiveFresh,
  type Concelho,
  type ConcelhoLive,
  type Distrito,
} from '../lib/concelhos';

const DAY = 24 * 60 * 60 * 1000;

/** Static concelho registry (fire history, geography, neighbours). Immutable per build. */
export function useConcelhos() {
  return useQuery({
    queryKey: ['concelhos-static'],
    queryFn: loadConcelhos,
    staleTime: DAY,
    gcTime: DAY,
  });
}

export function useDistritos() {
  return useQuery({
    queryKey: ['distritos-static'],
    queryFn: loadDistritos,
    staleTime: DAY,
    gcTime: DAY,
  });
}

/**
 * Live per-concelho risk snapshot: the 3h R2 bake (data.tutela.land), falling
 * back to the bundled build-time snapshot only if the CDN fetch fails.
 */
export function useConcelhosLive() {
  return useQuery({
    queryKey: ['concelhos-live'],
    queryFn: loadLiveFresh,
    staleTime: 3 * 60 * 60 * 1000,
  });
}

/** Convenience: everything a single concelho page needs, indexed by slug. */
export function useConcelho(slug: string): {
  concelho: Concelho | undefined;
  live: ConcelhoLive | undefined;
  distrito: Distrito | undefined;
  bySlug: Map<string, Concelho>;
  isLoading: boolean;
  isError: boolean;
} {
  const staticQ = useConcelhos();
  const liveQ = useConcelhosLive();
  const distQ = useDistritos();

  const bySlug = new Map((staticQ.data?.concelhos ?? []).map((c) => [c.slug, c]));
  const concelho = bySlug.get(slug);
  const live = liveQ.data?.concelhos.find((c) => c.slug === slug);
  const distrito = distQ.data?.distritos.find(
    (d) => d.slug === concelho?.distrito_slug,
  );

  return {
    concelho,
    live,
    distrito,
    bySlug,
    isLoading: staticQ.isLoading,
    isError: staticQ.isError,
  };
}
